import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { omit, xor } from "lodash";

import { GetTasksQueryParamsV3 } from "@fartherfinance/frontend/api/Tasks/requests/getTasks.v3";
import { TaskStatus } from "@fartherfinance/frontend/api/Tasks/Types";

import { usePersistedQueryParams } from "@src/multiCustodian/hooks/Advisor/usePersistedQueryParams";

import {
  TASK_STATUSES_ACTIVE,
  TASK_STATUSES_COMPLETED,
  TASKS_DEFAULT_QUERY_PARAMS,
} from "./common";
import { getFiltersFromTab, saveFiltersToTab } from "./storage";

interface TasksFiltersContextProps {
  queryParams: GetTasksQueryParamsV3;
  filters: Omit<GetTasksQueryParamsV3, "sort" | "pageSize" | "page">;
  showTaskStatusPill: boolean;
  taskStatusFilterCategory: "active" | "completed" | "all";
  handleShowTaskStatusPill: React.Dispatch<React.SetStateAction<boolean>>;
  handleChangeQueryParams: (newQueryParams: GetTasksQueryParamsV3) => void;
  handleResetQueryParams: (
    resetOverwriteQueryParams?: GetTasksQueryParamsV3
  ) => void;
}

const TasksFiltersContext = createContext<TasksFiltersContextProps | undefined>(
  undefined
);

export const TasksFiltersProviderV3: React.FC<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  // Get initial value from tab-specific storage or use default
  const getSavedQueryParams = (): GetTasksQueryParamsV3 => {
    // true indicates this is v3
    return getFiltersFromTab<GetTasksQueryParamsV3>(TASKS_DEFAULT_QUERY_PARAMS);
  };

  const [queryParams, setQueryParams] =
    usePersistedQueryParams<GetTasksQueryParamsV3>(getSavedQueryParams(), {
      parseBooleans: true,
      decode: false,
      arrayFormat: "bracket-separator",
    });

  const filterByTaskStatus = useMemo(
    () => queryParams.filterByTaskStatus ?? [],
    [queryParams]
  );

  const isDiffTaskStatusSetThanDefault =
    xor(filterByTaskStatus, TASKS_DEFAULT_QUERY_PARAMS.filterByTaskStatus)
      .length > 0;

  // NOTE: on initial start up we want to show task status pill only if it's different than default
  const [showTaskStatusPill, setShowTaskStatusPill] = useState(
    isDiffTaskStatusSetThanDefault
  );

  const taskStatusFilterCategory = useMemo(() => {
    const isActiveCategory =
      filterByTaskStatus.filter(
        (status) =>
          (TASK_STATUSES_ACTIVE as TaskStatus[]).indexOf(status) === -1
      ).length === 0;

    const isCompletedCategory =
      filterByTaskStatus.filter(
        (status) =>
          (TASK_STATUSES_COMPLETED as TaskStatus[]).indexOf(status) === -1
      ).length === 0;

    return isActiveCategory
      ? "active"
      : isCompletedCategory
      ? "completed"
      : "all";
  }, [filterByTaskStatus]);

  // Save filters to tab-specific storage whenever they change
  useEffect(() => {
    saveFiltersToTab(queryParams);
  }, [queryParams]);

  const handleChangeQueryParams = (
    newQueryParams: GetTasksQueryParamsV3
  ): void => {
    const updatedParams = { ...queryParams, ...newQueryParams };
    setQueryParams(updatedParams);
    // Tab storage update will happen in the useEffect
  };

  const handleResetQueryParams = (
    resetOverwriteQueryParams?: GetTasksQueryParamsV3
  ): void => {
    const resetParams = {
      ...TASKS_DEFAULT_QUERY_PARAMS,
      ...resetOverwriteQueryParams,
    };

    setQueryParams(resetParams);
    // Tab storage update will happen in the useEffect
  };

  return (
    <TasksFiltersContext.Provider
      value={{
        queryParams,
        filters: omit(queryParams, ["page", "pageSize", "sort"]),
        showTaskStatusPill,
        taskStatusFilterCategory,
        handleShowTaskStatusPill: setShowTaskStatusPill,
        handleChangeQueryParams,
        handleResetQueryParams,
      }}
    >
      {children}
    </TasksFiltersContext.Provider>
  );
};

export const useTasksFiltersV3 = () => {
  const context = useContext(TasksFiltersContext);

  if (!context) {
    throw new Error(
      "useTasksFilters v3 must be used within TasksFiltersContext"
    );
  }

  return context;
};
